import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatCard } from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Mic2, Building2, Disc3, Clock, LifeBuoy, DollarSign, CheckCircle2, ListMusic,
  PlusCircle, ShieldAlert, Wallet, Users2, TrendingUp, Flame,
} from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [subOpen, setSubOpen] = useState(false);
  const [artOpen, setArtOpen] = useState(false);
  const [sub, setSub] = useState({ name: "", login_email: "", login_password: "Admin@123" });
  const [art, setArt] = useState({ name: "" });

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setS(r.data));
  }, []);

  const isLabel = user.role === "label" && user.label_id;
  const isSolo = user.role === "artist" && !user.label_id;

  const createSub = async () => {
    try {
      await api.post("/sublabels", sub);
      toast.success("Sub-label requested — awaiting Super Admin approval");
      setSubOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const createArt = async () => {
    try {
      await api.post("/artists", { name: art.name });
      toast.success("Artist created");
      setArtOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const money = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const staff = ["super_admin", "operations", "accounts", "label_manager"].includes(user.role);

  const cards = [];
  if (s) {
    const R = (r) => () => nav(r);
    if (staff) {
      if (s.total_artists !== undefined) cards.push({ icon: Mic2, label: "Total Artists", value: s.total_artists, accent: "primary", testid: "stat-artists", onClick: R("/artists") });
      if (s.total_labels !== undefined) cards.push({ icon: Building2, label: "Total Labels", value: s.total_labels, accent: "blue", testid: "stat-labels", onClick: R("/labels") });
      cards.push({ icon: Disc3, label: "Total Releases", value: s.total_releases, accent: "purple", testid: "stat-releases", onClick: R("/releases") });
      cards.push({ icon: Clock, label: "Pending Releases", value: s.pending_releases, accent: "gold", testid: "stat-pending", onClick: R("/releases") });
      cards.push({ icon: LifeBuoy, label: "Open Tickets", value: s.open_tickets, accent: "rose", testid: "stat-tickets", onClick: R("/tickets") });
      cards.push({ icon: ListMusic, label: "Royalty Records", value: s.royalty_records, accent: "emerald", testid: "stat-royalty-records", onClick: R("/royalties") });
    } else if (user.role === "label") {
      cards.push({ icon: Mic2, label: "Your Artists", value: s.total_artists ?? 0, accent: "primary", testid: "stat-artists", onClick: R("/artists") });
      cards.push({ icon: Disc3, label: "Total Releases", value: s.total_releases, accent: "purple", testid: "stat-releases", onClick: R("/releases") });
      cards.push({ icon: Clock, label: "Pending", value: s.pending_releases, accent: "gold", testid: "stat-pending", onClick: R("/releases") });
      cards.push({ icon: DollarSign, label: "Royalty Total", value: money(s.royalty_total), accent: "emerald", testid: "stat-royalty-total", onClick: R("/royalties") });
      cards.push({ icon: LifeBuoy, label: "Open Tickets", value: s.open_tickets, accent: "rose", testid: "stat-tickets", onClick: R("/tickets") });
    } else {
      cards.push({ icon: Disc3, label: "Total Releases", value: s.total_releases, accent: "primary", testid: "stat-releases", onClick: R("/releases") });
      cards.push({ icon: Clock, label: "Pending", value: s.pending_releases, accent: "gold", testid: "stat-pending", onClick: R("/releases") });
      cards.push({ icon: CheckCircle2, label: "Approved", value: s.approved_releases, accent: "emerald", testid: "stat-approved", onClick: R("/releases") });
      cards.push({ icon: DollarSign, label: "Royalty Total", value: money(s.royalty_total), accent: "blue", testid: "stat-royalty-total", onClick: R("/royalties") });
      cards.push({ icon: LifeBuoy, label: "Open Tickets", value: s.open_tickets, accent: "rose", testid: "stat-tickets", onClick: R("/tickets") });
    }
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Welcome, {user.name}</h1>
          <p className="mt-1 text-muted-foreground">Here's an overview of your account.</p>
        </div>
        {(isLabel || isSolo) && (
          <div className="flex flex-wrap gap-2" data-testid="dashboard-actions">
            <Button onClick={() => nav("/releases/new")} data-testid="qa-new-release"><PlusCircle className="mr-2 h-4 w-4" />New Release</Button>
            <Button variant="secondary" onClick={() => nav("/takedowns")} data-testid="qa-new-takedown"><ShieldAlert className="mr-2 h-4 w-4" />New Takedown</Button>
            {isLabel && <Button variant="secondary" onClick={() => setSubOpen(true)} data-testid="qa-new-sublabel"><Building2 className="mr-2 h-4 w-4" />New Sub-Label</Button>}
            {isLabel && <Button variant="secondary" onClick={() => setArtOpen(true)} data-testid="qa-new-artist"><Users2 className="mr-2 h-4 w-4" />New Artist</Button>}
            <Button variant="secondary" onClick={() => nav("/withdrawals")} data-testid="qa-withdrawal"><Wallet className="mr-2 h-4 w-4" />Withdrawal</Button>
          </div>
        )}
      </div>

      <Dialog open={subOpen} onOpenChange={setSubOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create Sub-Label</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">A login is created for the sub-label but access needs Super Admin approval.</p>
          <div className="space-y-3">
            <div><Label>Sub-Label Name</Label><Input value={sub.name} onChange={(e) => setSub({ ...sub, name: e.target.value })} data-testid="sub-name" className="mt-1" /></div>
            <div><Label>Login Email</Label><Input value={sub.login_email} onChange={(e) => setSub({ ...sub, login_email: e.target.value })} data-testid="sub-email" className="mt-1" /></div>
            <div><Label>Login Password</Label><Input value={sub.login_password} onChange={(e) => setSub({ ...sub, login_password: e.target.value })} data-testid="sub-password" className="mt-1" /></div>
          </div>
          <DialogFooter><Button variant="secondary" onClick={() => setSubOpen(false)}>Cancel</Button><Button onClick={createSub} data-testid="create-sublabel-btn">Request Sub-Label</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={artOpen} onOpenChange={setArtOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Artist to Your Label</DialogTitle></DialogHeader>
          <div><Label>Artist Name</Label><Input value={art.name} onChange={(e) => setArt({ ...art, name: e.target.value })} data-testid="art-name" className="mt-1" /></div>
          <DialogFooter><Button variant="secondary" onClick={() => setArtOpen(false)}>Cancel</Button><Button onClick={createArt} data-testid="create-artist-btn">Create Artist</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      {!s ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            {cards.map((c) => (
              <StatCard key={c.testid} {...c} />
            ))}
          </div>
          {(isLabel || isSolo || (user.role === "artist")) && (s.lifetime_revenue !== undefined) && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="dashboard-highlights">
              <div className="rounded-xl border border-border bg-gradient-to-br from-emerald-500/10 to-transparent p-5" data-testid="hl-lifetime-revenue">
                <div className="flex items-center gap-2 text-emerald-400"><TrendingUp className="h-4 w-4" /><p className="text-xs font-medium uppercase tracking-wider">Lifetime Revenue</p></div>
                <p className="mt-2 text-3xl font-bold font-heading">{money(s.lifetime_revenue)}</p>
              </div>
              <div className="rounded-xl border border-border bg-gradient-to-br from-blue-500/10 to-transparent p-5" data-testid="hl-lifetime-paid">
                <div className="flex items-center gap-2 text-blue-400"><Wallet className="h-4 w-4" /><p className="text-xs font-medium uppercase tracking-wider">Lifetime Paid</p></div>
                <p className="mt-2 text-3xl font-bold font-heading">{money(s.lifetime_paid)}</p>
              </div>
              <div className="rounded-xl border border-border bg-gradient-to-br from-primary/10 to-transparent p-5" data-testid="hl-most-streamed">
                <div className="flex items-center gap-2 text-primary"><Disc3 className="h-4 w-4" /><p className="text-xs font-medium uppercase tracking-wider">Most-Streamed Track</p></div>
                <p className="mt-2 truncate text-xl font-bold font-heading">{s.most_streamed_track?.name || "—"}</p>
                <p className="text-sm text-muted-foreground">{s.most_streamed_track ? `${s.most_streamed_track.streams.toLocaleString("en-IN")} streams` : "No data yet"}</p>
              </div>
              <div className="rounded-xl border border-border bg-gradient-to-br from-amber-500/10 to-transparent p-5" data-testid="hl-trending">
                <div className="flex items-center gap-2 text-amber-400"><Flame className="h-4 w-4" /><p className="text-xs font-medium uppercase tracking-wider">Trending Track</p></div>
                <p className="mt-2 truncate text-xl font-bold font-heading">{s.trending_track?.name || "—"}</p>
                <p className="text-sm text-muted-foreground">{s.trending_track ? `${s.trending_track.streams.toLocaleString("en-IN")} streams · ${s.trending_track.period}` : "No data yet"}</p>
              </div>
            </div>
          )}
          {(isLabel || isSolo || user.role === "artist") && s.trend && s.trend.length > 0 && (
            <div className="rounded-xl border border-border bg-card p-5" data-testid="dashboard-trend-chart">
              <div className="mb-4 flex items-center gap-2 text-primary"><TrendingUp className="h-4 w-4" /><h3 className="font-heading text-lg font-semibold text-foreground">Growth Trend</h3></div>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={s.trend} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gStreams" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#f43f5e" stopOpacity={0.4} /><stop offset="95%" stopColor="#f43f5e" stopOpacity={0} /></linearGradient>
                    <linearGradient id="gRevenue" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10b981" stopOpacity={0.4} /><stop offset="95%" stopColor="#10b981" stopOpacity={0} /></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="period" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                    formatter={(val, name) => name === "revenue" ? [`₹${Number(val).toLocaleString("en-IN")}`, "Revenue"] : [Number(val).toLocaleString("en-IN"), "Streams"]} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Area yAxisId="left" type="monotone" dataKey="streams" stroke="#f43f5e" fill="url(#gStreams)" strokeWidth={2} name="streams" />
                  <Area yAxisId="right" type="monotone" dataKey="revenue" stroke="#10b981" fill="url(#gRevenue)" strokeWidth={2} name="revenue" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </div>
  );
}
